import { useEffect, useState } from 'react';
import { Badge, Pagination, Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import MercurXIcons from '../MercurXIcons/MercurXIcons';
import { mainColors } from '../../helpers/colors';
import { isNftSalesPath } from '../../helpers/salesRoute';
// import { Link } from 'react-router-dom';
import './MercurXTokenTable.scss';

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatUsd(value) {
  return toNumber(value).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: 4,
  });
}

function truncateMiddle(value, startLength, endLength) {
  const normalizedValue = `${value ?? ''}`.trim();

  if (!normalizedValue) {
    return '-';
  }

  if (normalizedValue.length <= startLength + endLength + 3) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, startLength)}...${normalizedValue.slice(-endLength)}`;
}

function MercurXTokenTable(props) {
  const { history, filteredTokens } = props;
  const pathname = history?.location?.pathname?.toLowerCase() || '';
  const isNftSalesPage = isNftSalesPath(pathname);

  const maxRowCountPerPage = 10;
  const maxShowingPage = 5;
  const [tokens, setTokens] = useState(filteredTokens ? filteredTokens : props.tokens);
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [renderedTokens, setRenderedTokens] = useState([]);
  const [renderedPages, setRenderedPages] = useState([]);
  const [lastPageOfPagination, setLastPageOfPagination] = useState(maxShowingPage);
  const copyToast = Swal.mixin({
    toast: true,
    position: 'top-end',
    iconColor: mainColors.primary,
    showConfirmButton: false,
    timer: 1800,
    timerProgressBar: true,
    customClass: {
      popup: 'sales-copy-toast',
      title: 'sales-copy-toast__title',
      timerProgressBar: 'sales-copy-toast__progress',
    },
  });

  useEffect(() => {
    setTokens(filteredTokens ? filteredTokens : props.tokens);
  }, [props.tokens, filteredTokens]);

  useEffect(() => {
    if (tokens) {
      setCurrentPage(1);
      const tempPages = [];
      for (let index = 0; index < Math.ceil(tokens.length / maxRowCountPerPage); index++) {
        tempPages.push(index + 1);
      }
      setPages([...tempPages]);
      setRenderedPages([
        ...tempPages.slice(lastPageOfPagination - maxShowingPage, lastPageOfPagination),
      ]);
    }
  }, [tokens]);

  useEffect(() => {
    if (tokens && currentPage) {
      const firstIndex = maxRowCountPerPage * currentPage - maxRowCountPerPage;
      const lastIndex = maxRowCountPerPage * currentPage;
      const tempRendered = tokens?.slice(firstIndex, lastIndex);
      setRenderedTokens([...tempRendered]);
    }
  }, [currentPage, tokens]);

  function scrollToTokensContainer() {
    const tokensContainer = document.getElementById('tokens-container');
    if (tokensContainer) {
      tokensContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const setPaginationPages = (page) => {
    if (
      renderedPages.findIndex((p) => p === page + 1) === -1 &&
      pages.slice(lastPageOfPagination + 1 - maxShowingPage, lastPageOfPagination + 1).length ===
      maxShowingPage
    ) {
      setLastPageOfPagination(lastPageOfPagination + 1);
      setRenderedPages(
        pages.slice(lastPageOfPagination + 1 - maxShowingPage, lastPageOfPagination + 1),
      );
    } else if (
      renderedPages.findIndex((p) => p === page - 1) === -1 &&
      pages.slice(lastPageOfPagination - 1 - maxShowingPage, lastPageOfPagination - 1).length ===
      maxShowingPage
    ) {
      setLastPageOfPagination(lastPageOfPagination - 1);
      setRenderedPages(
        pages.slice(lastPageOfPagination - 1 - maxShowingPage, lastPageOfPagination - 1),
      );
    }
  };

  const changePage = (page) => {
    setCurrentPage(page);
    if (page === lastPageOfPagination) {
      setPaginationPages(page);
    } else if (page === lastPageOfPagination - maxShowingPage + 1) {
      setPaginationPages(page);
    }
    scrollToTokensContainer();
  };

  const firstPage = () => {
    setCurrentPage(1);
    setLastPageOfPagination(maxShowingPage);
    setRenderedPages(pages.slice(0, maxShowingPage));
    scrollToTokensContainer();
  };

  const prevPage = () => {
    if (currentPage - 1 !== 0) {
      setCurrentPage(currentPage - 1);
      setPaginationPages(currentPage - 1);
      scrollToTokensContainer();
    }
  };

  const nextPage = () => {
    if (currentPage + 1 <= pages.length) {
      setCurrentPage(currentPage + 1);
      setPaginationPages(currentPage + 1);
      scrollToTokensContainer();
    }
  };

  const lastPage = () => {
    setCurrentPage(pages.length);
    if (pages.length > maxShowingPage) {
      setLastPageOfPagination(pages.length);
      setRenderedPages(pages.slice(pages.length - maxShowingPage, pages.length));
    }
    scrollToTokensContainer();
  };

  const TableSelectRow = (project) => {
    const projectId = project?.id;
    const isNftItem = project?.nft && !project?.token;

    if (!projectId) {
      return null;
    }

    return history.push(`/sales/${isNftItem ? 'nft' : 'token'}/project/${projectId}`);
  };

  function calculateFormat(x) {
    const formatBalance = (Math.floor((toNumber(x) / 10000) * 100) / 100).toString().split('.');

    const firstValueFormatBalance = Number(formatBalance[0]).toLocaleString('tr-TR');
    const secondValueFormatBalance = formatBalance[1] || '0';
    return firstValueFormatBalance + ',' + secondValueFormatBalance;
  }

  function getProjectImageSrc(projectId) {
    return `${process.env.REACT_APP_API_URL}/projects/${projectId}/logo`;
  }

  function onProjectImageError(event) {
    event.currentTarget.style.visibility = 'hidden';
  }

  function renderStatusBadge(project) {
    if (project?.is_active === 'active') {
      return <Badge bg="success">Active</Badge>;
    }
    if (project?.is_active === 'completed') {
      return <Badge bg="danger">Completed</Badge>;
    }
    return <Badge bg="secondary">Other</Badge>;
  }

  async function copyToClipboard(event, value, label) {
    event.stopPropagation();

    const normalizedValue = `${value ?? ''}`.trim();

    if (!normalizedValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(normalizedValue);
      await copyToast.fire({
        icon: 'success',
        title: `${label} copied to clipboard`,
      });
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Copy failed',
        text: `The ${label.toLowerCase()} could not be copied.`,
        confirmButtonColor: mainColors.primary,
        iconColor: '#E40039',
      });
    }
  }

  function renderCopyableCell(value, { label, startLength, endLength }) {
    const normalizedValue = `${value ?? ''}`.trim();

    if (!normalizedValue) {
      return '-';
    }

    return (
      <div
        className="sales-copy-cell"
        role="button"
        tabIndex={0}
        title={`${normalizedValue}\nClick to copy`}
        onClick={(event) => copyToClipboard(event, normalizedValue, label)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            copyToClipboard(event, normalizedValue, label);
          }
        }}
      >
        <span className="sales-copy-cell__value" title={normalizedValue}>
          {truncateMiddle(normalizedValue, startLength, endLength)}
        </span>
        <span className="sales-copy-cell__icon" aria-hidden="true">
          <MercurXIcons name="BiCopy" color={mainColors.primary} size="15" />
        </span>
      </div>
    );
  }

  const renderedSaleItems = renderedTokens?.filter((project) => {
    const saleAsset = project?.token || project?.nft;
    return Boolean(saleAsset?.status);
  });

  return (
    <>
      <Table className="sales-table-design" responsive hover borderless>
        <thead>
          <tr className="text-t-body-color text-fs-tag">
            <th>#</th>
            <th></th>
            {isNftSalesPage ? (
              <>
                <th>NFT Name</th>
                <th>NFT Address</th>
                <th>NFT URL</th>
                <th>NFT Price in MERX</th>
                <th>NFT Price in USD</th>
                <th>Distribution</th>
                <th>Total Supply</th>
              </>
            ) : (
              <>
                <th>Token Name</th>
                <th>Token Symbol</th>
                <th>Token Price in MERX</th>
                <th>Token Price in USD</th>
                <th>Total Raised</th>
                <th>Target Raised</th>
                <th>Total Token Sold</th>
                <th>Distribution</th>
                <th>Total Supply</th>
              </>
            )}
            <th>Status</th>
          </tr>
        </thead>
        {renderedSaleItems?.length ? (
          <tbody>
            {renderedSaleItems.map((project, index) => {
              const saleAsset = project?.token || project?.nft;

              return (
                <tr
                  key={project?.id || index}
                  onClick={() => {
                    TableSelectRow(project);
                  }}
                  className="text-t-head-color"
                >
                  <td>{index + 1}</td>
                  <td>
                    <img
                      src={getProjectImageSrc(project?.id)}
                      alt={`${project?.name || 'project'} logo`}
                      className="sales-item-logo"
                      onError={onProjectImageError}
                    />
                  </td>
                  {isNftSalesPage ? (
                    <>
                      <td>{saleAsset?.name || project?.name || '-'}</td>
                      <td>
                        {renderCopyableCell(saleAsset?.address, {
                          label: 'NFT address',
                          startLength: 8,
                          endLength: 6,
                        })}
                      </td>
                      <td>
                        {renderCopyableCell(saleAsset?.url, {
                          label: 'NFT URL',
                          startLength: 22,
                          endLength: 14,
                        })}
                      </td>
                      <td>{toNumber(saleAsset?.price_in_mercurx)}</td>
                      <td>{formatUsd(saleAsset?.price_in_usd)}</td>
                      <td>{calculateFormat(saleAsset?.distribution)}</td>
                      <td>{calculateFormat(saleAsset?.total_supply)}</td>
                    </>
                  ) : (
                    <>
                      <td>{saleAsset?.name || project?.name || '-'}</td>
                      <td>{saleAsset?.symbol || '-'}</td>
                      <td>{toNumber(saleAsset?.price_in_mercurx)}</td>
                      <td>{formatUsd(saleAsset?.price_in_usd)}</td>
                      <td>{formatUsd(project?.total_raised)}</td>
                      <td>{formatUsd(project?.target_raised)}</td>
                      <td>{calculateFormat(project?.total_tokens_sold)}</td>
                      <td>{calculateFormat(saleAsset?.distribution)}</td>
                      <td>{calculateFormat(saleAsset?.total_supply)}</td>
                    </>
                  )}
                  <td>{renderStatusBadge(project)}</td>
                </tr>
              );
            })}
          </tbody>
        ) : null}
      </Table>

      {!renderedSaleItems?.length && (
        <div className="d-flex text-fs-body-md text-t-body-color justify-content-center">
          {isNftSalesPage
            ? 'No NFT found according to search results.'
            : 'No token found according to search results.'}
        </div>
      )}

      {pages?.length > 1 ? (
        <Pagination className="d-flex justify-content-center">
          <Pagination.First onClick={() => firstPage()} />
          <Pagination.Prev onClick={() => prevPage()} />
          {renderedPages.map((page, index) => {
            return (
              <Pagination.Item
                key={index}
                active={page === currentPage}
                onClick={() => changePage(page)}
              >
                {page}
              </Pagination.Item>
            );
          })}
          <Pagination.Next onClick={() => nextPage()} />
          <Pagination.Last onClick={() => lastPage()} />
        </Pagination>
      ) : null}
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    tokens: state.tokenReducer.tokens,
  };
};

export default connect(mapStateToProps)(MercurXTokenTable);
